import { useEffect, useState } from 'react';
import api from '../axiosInstance';

const STATUS_IDLE = 'idle';
const STATUS_LOADING = 'loading';
const STATUS_READY = 'ready';
const STATUS_ERROR = 'error';

export const WORKFLOW_SCHEMA_STATUS = {
  IDLE: STATUS_IDLE,
  LOADING: STATUS_LOADING,
  READY: STATUS_READY,
  ERROR: STATUS_ERROR,
};

let schemaState = { status: STATUS_IDLE, data: null, error: null };
let fetchPromise = null;
let cachedEtag = null;
const listeners = new Set();

function emit() {
  const snapshot = schemaState;
  listeners.forEach((listener) => {
    try {
      listener(snapshot);
    } catch (err) {
      console.error('[workflow-schema] listener error', err);
    }
  });
}

function setState(partial) {
  schemaState = { ...schemaState, ...partial };
  emit();
}

function extractErrorCode(response) {
  if (!response) return 'workflow_schema_error';
  if (response.data && typeof response.data.error === 'string') return response.data.error;
  if (response.status === 503) return 'workflow_schema_unavailable';
  return 'workflow_schema_error';
}

function deepClone(obj) {
  if (obj == null || typeof obj !== 'object') return obj;
  if (typeof structuredClone === 'function') {
    try { return structuredClone(obj); } catch (_) {}
  }
  try { return JSON.parse(JSON.stringify(obj)); } catch (_) { return obj; }
}

function resolveConditionalLookupTarget(lookup, condId) {
  if (!lookup || !condId) return null;
  const entry = lookup.get(condId);
  if (!entry) return null;
  if (entry && typeof entry === 'object' && Object.prototype.hasOwnProperty.call(entry, 'component')) {
    return entry;
  }
  return { component: entry, index: null };
}

function cloneConditionalChild(component, suffix) {
  const clone = deepClone(component);
  if (!clone) return clone;
  if (clone.id) clone.id = `${clone.id}${suffix}`;
  if (clone.props?.id) clone.props.id = `${clone.props.id}${suffix}`;
  return clone;
}

function optionRevealChildren(opt) {
  if (Array.isArray(opt?.children)) return opt.children;
  if (opt?.conditional && Array.isArray(opt.conditional.children)) return opt.conditional.children;
  return [];
}

function inflateOptionChildren(component, lookup, { consumed = null, trail = new Set(), counterRef = { value: 0 } } = {}) {
  if (!component || typeof component !== 'object' || !lookup) return component;
  const nextComponent = { ...component };
  if (Array.isArray(component.children)) {
    nextComponent.children = component.children.map((child) =>
      inflateOptionChildren(child, lookup, { consumed, trail, counterRef })
    );
  }
  const type = String(component.type || component.template_key || '').toLowerCase();
  if (!['radio', 'radios', 'checkbox', 'checkboxes'].includes(type)) return nextComponent;
  const key = Array.isArray(component.options) ? 'options' : (Array.isArray(component.items) ? 'items' : null);
  if (!key) return nextComponent;
  nextComponent[key] = component[key].map((opt) => {
    if (!opt || typeof opt !== 'object') return opt;
    const updated = { ...opt };
    let children = optionRevealChildren(opt).map((child) =>
      inflateOptionChildren(child, lookup, { consumed, trail, counterRef })
    );
    const condId = opt.conditionalChildId ? String(opt.conditionalChildId) : '';
    if (!children.length && condId && !trail.has(condId)) {
      const target = resolveConditionalLookupTarget(lookup, condId);
      if (target?.component && target.component !== component && (!consumed || !consumed.has(target.index))) {
        const suffix = `__cond${++counterRef.value}`;
        const nextTrail = new Set(trail);
        nextTrail.add(condId);
        const childClone = cloneConditionalChild(target.component, suffix);
        if (childClone) {
          children = [
            inflateOptionChildren(childClone, lookup, { consumed, trail: nextTrail, counterRef }),
          ];
          if (consumed && target.index != null) consumed.add(target.index);
          delete updated.conditionalChildId;
        }
      }
    }
    if (children.length) {
      updated.children = children;
      delete updated.conditionalChildId;
    }
    return updated;
  });
  return nextComponent;
}

function expandConditionalChildren(schema) {
  if (!Array.isArray(schema)) return schema;
  return schema.map((step) => {
    if (!Array.isArray(step?.components)) return step;
    const stepClone = deepClone(step);
    const comps = stepClone.components || [];
    const lookup = new Map();
    comps.forEach((comp, idx) => {
      if (!comp) return;
      const keys = [];
      if (comp.name) keys.push(String(comp.name));
      if (comp.storageKey) keys.push(String(comp.storageKey));
      if (comp.id != null) keys.push(String(comp.id));
      if (comp.props?.name) keys.push(String(comp.props.name));
      if (comp.props?.id != null) keys.push(String(comp.props.id));
      keys.forEach((key) => {
        if (!lookup.has(key)) lookup.set(key, { component: comp, index: idx });
      });
    });

    const consumed = new Set();
    const counterRef = { value: 0 };
    stepClone.components = comps.map((comp) =>
      inflateOptionChildren(comp, lookup, { consumed, counterRef })
    );

    if (consumed.size) {
      stepClone.components = stepClone.components.filter((_, idx) => !consumed.has(idx));
    }
    return stepClone;
  });
}

async function requestSchema({ force = false } = {}) {
  const headers = {};
  if (!force && cachedEtag) {
    headers['If-None-Match'] = cachedEtag;
  }

  let response;
  try {
    response = await api.get('/api/runtime/workflow-schema', {
      headers,
      validateStatus: () => true,
    });
  } catch (err) {
    const code = err?.response?.data?.error || 'workflow_schema_unavailable';
    cachedEtag = null;
    setState({ status: STATUS_ERROR, data: null, error: code });
    throw err;
  }

  if (response.status === 200 && response.data) {
    const headers = response.headers || {};
    const headerEtag = headers.etag || headers.ETag || headers['etag'];
    if (headerEtag) cachedEtag = headerEtag;
    const normalized = deepClone(response.data);
    if (normalized && Array.isArray(normalized.schema)) {
      normalized.schema = expandConditionalChildren(normalized.schema);
    }
    setState({ status: STATUS_READY, data: normalized, error: null });
    return schemaState;
  }

  if (response.status === 304 && schemaState.data) {
    setState({ status: STATUS_READY, error: null });
    return schemaState;
  }

  cachedEtag = null;
  const code = extractErrorCode(response);
  setState({ status: STATUS_ERROR, data: null, error: code });
  throw new Error(code);
}

function fetchWorkflowSchema({ force = false } = {}) {
  if (fetchPromise) {
    return fetchPromise;
  }
  if (!schemaState.data) {
    setState({ status: STATUS_LOADING, error: null });
  }
  fetchPromise = requestSchema({ force })
    .catch((err) => {
      return Promise.reject(err);
    })
    .finally(() => {
      fetchPromise = null;
    });
  return fetchPromise;
}

function ensureSchema({ force = false } = {}) {
  if (schemaState.status === STATUS_READY && !force) {
    return Promise.resolve(schemaState);
  }
  return fetchWorkflowSchema({ force }).catch(() => schemaState);
}

export function useWorkflowSchema(options = {}) {
  const [state, setLocalState] = useState(schemaState);

  useEffect(() => {
    const listener = (next) => setLocalState(next);
    listeners.add(listener);
    return () => listeners.delete(listener);
  }, []);

  useEffect(() => {
    if (options.autoFetch === false) return;
    ensureSchema({ force: options.force === true });
  }, [options.autoFetch, options.force]);

  return {
    status: state.status,
    schema: state.data?.schema || null,
    runtime: state.data,
    error: state.error,
    refresh: () => fetchWorkflowSchema({ force: true }),
  };
}

export function getWorkflowSchemaState() {
  return schemaState;
}

export function prefetchWorkflowSchema(options = {}) {
  return ensureSchema(options);
}

export function resetWorkflowSchemaCache() {
  schemaState = { status: STATUS_IDLE, data: null, error: null };
  cachedEtag = null;
  if (fetchPromise) {
    fetchPromise = null;
  }
  emit();
}
